{
  "meta": {
    "manager": {
      "name": "Morgan Ellis",
      "first_name": "Morgan"
    },
    "report": {
      "name": "Avery Chen",
      "first_name": "Avery",
      "role": "Product Designer",
      "tenure": "~11 months"
    },
    "period": {
      "start": "2025-07-14",
      "end": "2026-02-24"
    },
    "generated": "2026-03-06",
    "meeting_count": 7,
    "work_summary_count": 8
  },
  "dashboard": [
    {
      "dimension": "Career Growth",
      "icon": "🚀",
      "status": "critical",
      "status_label": "Stalled",
      "detail": "7 months, no development plan"
    },
    {
      "dimension": "Blockers & Support",
      "icon": "🚧",
      "status": "alert",
      "status_label": "Slow Response",
      "detail": "Ad-hoc blocker took 4 months"
    },
    {
      "dimension": "Workload Balance",
      "icon": "⚖️",
      "status": "critical",
      "status_label": "Burnout Risk",
      "detail": "Working evenings, 3 workstreams"
    },
    {
      "dimension": "Psychological Safety",
      "icon": "🛡️",
      "status": "warn",
      "status_label": "Eroding",
      "detail": "Raised concerns met with delays"
    },
    {
      "dimension": "Feedback & Recognition",
      "icon": "🏆",
      "status": "alert",
      "status_label": "Recognition Gap",
      "detail": "Launch win went uncelebrated"
    },
    {
      "dimension": "Decision Making",
      "icon": "⚡",
      "status": "warn",
      "status_label": "Slow Decisions",
      "detail": "Key topics loop 3+ meetings"
    },
    {
      "dimension": "Autonomy Delegation",
      "icon": "🎯",
      "status": "good",
      "status_label": "Mostly Healthy",
      "detail": "Report drives design decisions"
    },
    {
      "dimension": "Energy Alignment",
      "icon": "🔋",
      "status": "warn",
      "status_label": "Draining Mix",
      "detail": "Ad-hoc and bugs sap energy"
    },
    {
      "dimension": "Frequency Health",
      "icon": "📅",
      "status": "alert",
      "status_label": "Irregular",
      "detail": "Avg 33 days between meetings"
    },
    {
      "dimension": "Meeting Structure",
      "icon": "📋",
      "status": "warn",
      "status_label": "Improvable",
      "detail": "Career topics get cut, no action tracking"
    },
    {
      "dimension": "Personal Connection",
      "icon": "💬",
      "status": "warn",
      "status_label": "Needs Attention",
      "detail": "Zero personal touchpoints found"
    },
    {
      "dimension": "Talk Ratio",
      "icon": "🎙️",
      "status": "good",
      "status_label": "Balanced",
      "detail": "Avery drives most discussion"
    },
    {
      "dimension": "Team Dynamics",
      "icon": "👥",
      "status": "warn",
      "status_label": "Narrow Network",
      "detail": "Only 1 close collaborator (Suki)"
    },
    {
      "dimension": "Expectations Tracker",
      "icon": "📊",
      "status": "warn",
      "status_label": "Gaps",
      "detail": "2 goals active, review not shared"
    }
  ],
  "dimensions": [
    {
      "name": "Career Growth",
      "icon": "🚀",
      "status": "critical",
      "insights": [
        {
          "title": "Promotion question unanswered for 7 months",
          "priority": "high",
          "body": "Avery first raised the senior designer promotion in September 2025. It has been discussed in three separate meetings — September 8, November 17, and February 24 — without a concrete answer, a written development plan, or a clear rubric for what's needed. In the most recent meeting, Avery directly asked 'Am I being considered?' and Morgan responded with 'let me check with my manager.' This pattern of deferral is the single highest retention risk in the data.",
          "evidence": "Avery's note on Feb 24: 'Seven months of asking and I still don't have a straight answer. I love the work here but I'm starting to look at what's out there.'",
          "action": {
            "label": "Open Next Meeting With",
            "text": "Avery, I owe you an apology. You've been asking about your path to senior designer since September and I haven't given you a real answer. That's not OK. I want to fix that today. Here's what I know about where you stand, here's what I think the gaps are — if any — and here's what I'm going to do to advocate for you.",
            "variant": "default"
          }
        },
        {
          "title": "Vague feedback masquerading as career guidance",
          "priority": "high",
          "body": "When career growth was finally discussed in November, the feedback was 'consistent impact and maybe leading a project end-to-end.' But Avery has already led the onboarding redesign end-to-end — from discovery through research through handoff through launch — delivering an 18% activation lift. The gap between the advice and the reality suggests Morgan hasn't mapped Avery's actual accomplishments against the promotion criteria. Avery noticed this disconnect immediately.",
          "evidence": "Avery's note on Nov 17: 'Consistent impact — haven't I been doing that? I still don't know what the gap is between where I am and senior level.'",
          "action": {
            "label": "Manager Action",
            "text": "Before the next 1-on-1, pull the senior designer rubric (or create one if it doesn't exist), map Avery's accomplishments against each criterion, and identify the specific 1-2 gaps — if any. Come to the meeting with a written development plan or a promotion recommendation, not platitudes.",
            "variant": "default"
          }
        },
        {
          "title": "Performance review never shared",
          "priority": "medium",
          "body": "In January, Avery asked about the perf review outcome. Morgan said it 'went well' and promised to forward the written review when HR sends it. As of the last meeting on February 24, there's no indication it was ever shared. Withholding or delaying performance feedback — even unintentionally — makes the report feel like their growth isn't a priority.",
          "evidence": "Avery's note on Jan 12: 'Still haven't seen my written review. Still no development plan.'",
          "action": {
            "label": "Manager Action",
            "text": "Send Avery the written performance review today — before the next 1-on-1. Don't wait for it to come up again. A simple message: 'I realized I never sent you your written review. That's on me. Here it is. Let's discuss at our next meeting.'",
            "variant": "default"
          }
        },
        {
          "title": "No growth-oriented goals exist",
          "priority": "medium",
          "body": "All three of Avery's goals are delivery-focused: ship the onboarding redesign, complete settings page, build design system. None explicitly target skill development, leadership growth, or career progression. For someone actively seeking promotion, at least one goal should articulate what senior-level looks like and create a path to demonstrate it.",
          "evidence": null,
          "action": {
            "label": "Suggested Discussion Topic",
            "text": "Avery, I want to create a development goal specifically focused on your growth toward senior designer. What skills or experiences do you think would be most valuable? I'm thinking something like leading a cross-functional initiative or mentoring — but I want your input on what would be most meaningful to you.",
            "variant": "default"
          }
        }
      ]
    },
    {
      "name": "Workload Balance",
      "icon": "⚖️",
      "status": "critical",
      "insights": [
        {
          "title": "Active burnout in progress",
          "priority": "high",
          "body": "Avery explicitly flagged burnout concerns in the February 24 meeting and disclosed working evenings to keep up. The response — 'take a long weekend' — addresses recovery without addressing the root cause: too many concurrent workstreams. A long weekend doesn't reduce the backlog; it just delays it. Avery's own note captures this perfectly. This is the moment where a manager needs to cut scope, not prescribe rest.",
          "evidence": "Avery's note on Feb 24: 'A long weekend won't fix this. I need fewer things on my plate, not a break from all of them. I wish Morgan would actually cut scope or bring in help.'",
          "action": {
            "label": "Open Next Meeting With",
            "text": "Avery, I dropped the ball when you told me you were burning out. Suggesting a long weekend was a band-aid, not a fix. Let's actually look at your plate and make real cuts. What if we pause the settings page bugs and I find someone else to handle the remaining ad-hoc leakage? Your design system work is the highest leverage — let's protect it.",
            "variant": "default"
          }
        },
        {
          "title": "Three concurrent workstreams with no prioritization",
          "priority": "high",
          "body": "Avery is simultaneously carrying the design system (40% done, due April 30), settings page phase 2, and residual settings page bug fixes — plus ad-hoc requests that still leak through the intake process. The work summary from Feb 17-23 confirms evenings are being used to maintain pace on the design system. This is a textbook overload pattern. The manager's job is to protect capacity by saying no on Avery's behalf.",
          "evidence": "Work summary Feb 17-23: 'Quality is high but pace is slower than projected — carrying too many parallel workstreams. Working evenings to stay on track.'",
          "action": {
            "label": "Manager Action",
            "text": "Before the next 1-on-1, decide which workstream gets deprioritized. The design system is the highest-growth opportunity and Avery's strongest energy area. Settings page bugs should be handed off or batched. Come to the meeting with a proposal, not a question — Avery shouldn't have to be the one cutting their own scope.",
            "variant": "default"
          }
        },
        {
          "title": "Context-switching load consistently high since July",
          "priority": "medium",
          "body": "Looking across the full timeline, Avery has been juggling 2-3 distinct workstreams nearly every week since July 2025. The early weeks had onboarding + ad-hoc requests. By fall it was onboarding + settings page + ad-hoc. Now it's design system + settings bugs + residual ad-hoc. There has been no sustained period of single-project focus. This is expensive: research suggests context-switching can consume 20-40% of productive time.",
          "evidence": "Work summary Jul 7-13 shows onboarding + 2 ad-hoc requests (6 hrs). Work summary Nov 10-16 shows settings page + onboarding support + ad-hoc requests (5 hrs).",
          "action": {
            "label": "Suggested Discussion Topic",
            "text": "Avery, if you could only work on one thing for the next two weeks with zero interruptions, what would it be? I want to try to actually make that happen — even if it means I have to absorb or redirect the other requests.",
            "variant": "default"
          }
        }
      ]
    },
    {
      "name": "Blockers & Support",
      "icon": "🚧",
      "status": "alert",
      "insights": [
        {
          "title": "Ad-hoc sales requests: 4-month resolution cycle damaged trust",
          "priority": "high",
          "body": "Avery first raised the ad-hoc sales request problem on August 11. It was discussed in four consecutive meetings — August 11, September 8, October 6, and November 17 — before Morgan established an intake process. During those 4 months, Avery was losing 8-10 hours per week to unplanned work. When Morgan suggested Avery 'start saying no directly' without providing managerial cover, it felt like the burden was being pushed back onto the report. The eventual resolution was good, but the delay sent a message: raising issues here doesn't lead to fast action.",
          "evidence": "Avery's note on Oct 6: 'I don't feel comfortable pushing back on the sales VP without Morgan backing me up explicitly. This has been going on for 3 months now.' Note on Nov 17: 'Took 4 months but better late than never.'",
          "action": {
            "label": "Suggested Discussion Topic",
            "text": "Avery, I want to acknowledge something. The ad-hoc request problem took way too long to fix — 4 months from when you first raised it. That's not the response time you deserve. Going forward, when you flag a blocker, I'm committing to having at least a concrete next step within one week. If I can't solve it immediately, I'll tell you what I'm doing and by when. Does that sound right?",
            "variant": "default"
          }
        },
        {
          "title": "Ad-hoc requests still leaking through the new process",
          "priority": "medium",
          "body": "Despite the intake process established in November, the Feb 24 meeting and work summaries indicate requests are still slipping through. The process reduced volume but didn't eliminate it. This is a partially-resolved blocker that needs a second pass — either stricter enforcement or a different routing mechanism.",
          "evidence": "Meeting Feb 24: 'still getting occasional ad-hoc requests that slip through the intake process.' Work summary Nov 10-16: 'Still getting 2-3 ad-hoc sales requests per week.'",
          "action": {
            "label": "Manager Action",
            "text": "Audit the intake process: who is bypassing it and why? Have a direct conversation with the sales VP to reinforce that all design requests must go through the form. Consider giving Avery explicit permission and language to redirect: 'Please submit through the intake form — Morgan triages weekly.'",
            "variant": "default"
          }
        },
        {
          "title": "Morgan didn't attend any user research sessions",
          "priority": "low",
          "body": "Morgan offered to sit in on one of the five user research sessions but didn't commit to a specific one and ultimately didn't attend any. Avery ran all sessions solo — which demonstrated capability — but Avery noted wishing Morgan had been present. When a manager offers support and doesn't follow through, it's worse than not offering, because it creates an expectation and then breaks it.",
          "evidence": "Avery's note on Sep 8: 'Really proud of these insights. First time running research end-to-end on my own. Wish Morgan had sat in on at least one session though.'",
          "action": {
            "label": "Suggested Discussion Topic",
            "text": "Avery, I want to call out that I said I'd sit in on one of your research sessions and then didn't. That was a dropped commitment on my part. Your research was excellent — but I should have shown up. For the settings page user testing or any future research, I want to be there. Can you loop me in when you schedule the next round?",
            "variant": "default"
          }
        }
      ]
    },
    {
      "name": "Psychological Safety",
      "icon": "🛡️",
      "status": "warn",
      "insights": [
        {
          "title": "Avery raises concerns consistently — but follow-through is slow",
          "priority": "high",
          "body": "The data shows Avery has strong psychological safety to raise issues: they flagged ad-hoc requests, burnout, career frustration, and workload concerns directly. This is excellent — many reports wouldn't. The problem is on the response side. When concerns are raised repeatedly without resolution, the report learns that speaking up is performative. The concern-to-action cycle is too slow: ad-hoc requests took 4 months, career growth is still unresolved after 7 months, burnout was met with a surface-level suggestion. Avery's willingness to keep raising issues is eroding.",
          "evidence": "Avery's note on Feb 24: 'Seven months of asking and I still don't have a straight answer.' This is someone who started by trusting the process and is now losing faith in it.",
          "action": {
            "label": "Suggested Discussion Topic",
            "text": "Avery, I want you to know that I really value that you bring things to me directly — the ad-hoc request issue, the burnout concern, the career questions. That takes trust and I don't take it for granted. I also know my follow-through hasn't matched your honesty. I'm working on that. Please keep telling me when something's not working.",
            "variant": "default"
          }
        },
        {
          "title": "Avery's private notes reveal a growing gap between what's said and what's felt",
          "priority": "high",
          "body": "Across meetings, Avery's individual notes consistently reveal deeper frustration than what surfaces in the conversation. The onboarding launch note ('a bit disappointed Morgan didn't suggest sharing this at all-hands'), the burnout note ('I wish Morgan would actually cut scope'), and the career note ('starting to look at what's out there') all indicate that Avery is self-editing — sharing the polite version in the meeting and saving the real version for private notes. This is a sign that safety is adequate for raising topics but not deep enough for full candor.",
          "evidence": "Pattern across Avery's individual notes: consistently expressing stronger frustration privately than in the meeting discussion.",
          "action": {
            "label": "Suggested Discussion Topic",
            "text": "Avery, I want to check something with you. When you bring up concerns in our meetings, are you holding back to be diplomatic? I genuinely want the unfiltered version. If you think I'm not handling something well, I'd rather hear it bluntly than find out later. What's the thing you've been most hesitant to say directly?",
            "variant": "default"
          }
        }
      ]
    },
    {
      "name": "Feedback & Recognition",
      "icon": "🏆",
      "status": "alert",
      "insights": [
        {
          "title": "Onboarding launch — a major win with no visible celebration",
          "priority": "high",
          "body": "The onboarding redesign exceeded its target: 18% activation rate improvement vs. the 15% goal, with email verification drop-off cut from 30% to 12%. This is a signature accomplishment — end-to-end ownership from discovery through research through design through launch. Morgan congratulated Avery in the 1-on-1 but didn't suggest broader visibility: no all-hands mention, no write-up, no company-wide recognition. For someone seeking a senior promotion, visibility for wins of this magnitude is essential.",
          "evidence": "Avery's note on Jan 12: 'Really proud of these numbers. A bit disappointed Morgan didn't suggest sharing this at all-hands or writing it up. I feel like this work is invisible outside our team.'",
          "action": {
            "label": "Manager Action",
            "text": "Write up the onboarding redesign results and share it company-wide — or better yet, ask Avery to present it at the next all-hands. Frame it as: 'Avery led this end-to-end and I want the whole company to see the impact.' This serves double duty: recognition and promotion evidence.",
            "variant": "default"
          }
        },
        {
          "title": "Only one formal recognition in 8 months",
          "priority": "medium",
          "body": "There is exactly one recognition in the data: the Figma handoff quality recognition from October. While it was specific and well-deserved, there have been multiple other recognition-worthy moments that went unacknowledged: completing user research independently, the launch metrics, the settings page positive feedback, and the design system collaboration with Suki. A cadence of one recognition per 8 months is far below the recommended frequency.",
          "evidence": "Recognition data shows only one entry (Figma handoff). No recognition for launch results, user research, or settings page work.",
          "action": {
            "label": "Manager Action",
            "text": "Create a recognition for the onboarding launch results today. Then commit to recognizing Avery's work at least monthly — even informally. Specific recognition ('Your research report was so thorough that engineering could build directly from it') is more powerful than generic praise ('great job').",
            "variant": "default"
          }
        },
        {
          "title": "No constructive feedback on record",
          "priority": "medium",
          "body": "All feedback in the data is positive: 'great direction,' 'on the right track,' 'doing great work.' While Avery's output quality is genuinely high, the absence of any constructive feedback means growth opportunities are being missed. Even top performers benefit from specific developmental input. The vague 'consistent impact' comment in November wasn't constructive feedback — it was ambiguous goalpost-setting.",
          "evidence": null,
          "action": {
            "label": "Suggested Discussion Topic",
            "text": "Avery, your work quality has been excellent and I want to be more intentional about helping you grow. One area I'd push on: the design system documentation could become a template for the whole team. What if we used it as a chance to practice defining and communicating design standards to non-designers? That's a senior-level skill that would strengthen your promotion case.",
            "variant": "default"
          }
        }
      ]
    },
    {
      "name": "Decision Making",
      "icon": "⚡",
      "status": "warn",
      "insights": [
        {
          "title": "Career path decision has looped across 4 meetings with no resolution",
          "priority": "high",
          "body": "The senior designer promotion question was raised on Sep 8, Nov 17, Jan 12, and Feb 24. Each time, the conversation ended with a deferral: 'we should talk about that,' 'consistent impact,' 'it went well,' 'let me check.' This is the textbook discussion loop: it feels like progress because the topic comes up, but no actual decision is ever made. After 4 loops, the topic must be decided — even if the answer is 'not this cycle, and here's specifically why.'",
          "evidence": "Sep 8: 'we should definitely talk about that' (tabled). Nov 17: vague criteria. Jan 12: no specifics shared. Feb 24: 'let me check with my manager.'",
          "action": {
            "label": "Manager Action",
            "text": "Apply the two-week rule: this topic has been discussed four times. It must be decided before the next 1-on-1. Either Avery is being put forward for promotion (with a timeline) or they're not (with specific gaps and a plan). Get the answer from your manager this week.",
            "variant": "default"
          }
        },
        {
          "title": "Workload prioritization discussed but never decided",
          "priority": "medium",
          "body": "In the July meeting, Avery's three concurrent projects were acknowledged but no prioritization was set. In October, settings page delay was noted but no timeline discussion occurred. In February, burnout was raised but no scope was cut. The pattern: workload is discussed sympathetically but never decisively. A clear 'here's what's #1, here's what we're dropping' has never emerged from any meeting.",
          "evidence": "Jul 14: '3 active projects acknowledged but no prioritization discussed.' Feb 24: 'Suggested a long weekend but didn't discuss workload redistribution or deprioritization.'",
          "action": {
            "label": "Suggested Discussion Topic",
            "text": "Avery, I've been acknowledging your workload without actually making decisions about it. Let's fix that now. I'm proposing: design system is priority one, settings page phase 2 is on hold until April, and I'm handling all remaining ad-hoc leakage. Push back if you disagree — but let's leave this meeting with a clear stack rank.",
            "variant": "default"
          }
        }
      ]
    },
    {
      "name": "Energy Alignment",
      "icon": "🔋",
      "status": "warn",
      "insights": [
        {
          "title": "High energy: research, system thinking, and craft",
          "priority": "positive",
          "body": "Avery's strongest energy signals cluster around three areas: user research (ran sessions independently, wrote a 12-page report, expressed pride), design systems (self-initiated atomic design methodology, partnering with engineering), and craft quality (the Figma handoff was described as 'the most thorough we've received'). These are the activities where Avery speaks with enthusiasm, takes initiative, and goes above and beyond the minimum.",
          "evidence": "Avery's note Sep 8: 'Really proud of these insights. First time running research end-to-end on my own.' Design system was the one goal Avery created (vs. Morgan creating the others).",
          "action": {
            "label": "Keep Doing",
            "text": "Avery's energy areas — research, systems thinking, and design craft — are exactly the things that differentiate a senior designer. Lean into these when assigning work and building the promotion case.",
            "variant": "green"
          }
        },
        {
          "title": "Energy drain: ad-hoc requests and bug fixes",
          "priority": "medium",
          "body": "Ad-hoc sales requests and settings page bug fixes are consistently described with frustration or mentioned minimally. These are low-creative-autonomy tasks that interrupt deep work. While some bug fixing is inevitable, it should be minimized for someone whose energy comes from design thinking and research. The combination of draining work + high volume is compounding the burnout risk.",
          "evidence": "Multiple work summaries quantify ad-hoc time (5-6 hrs/week). Bug fixes described as 'unplanned work but necessary.'",
          "action": {
            "label": "Suggested Discussion Topic",
            "text": "Avery, I notice you come alive when you're doing research and systems work, and the ad-hoc stuff drains you. What if we made it a goal to get your time on energizing work above 80%? What's the minimum amount of maintenance work we can't avoid, and how do we contain the rest?",
            "variant": "default"
          }
        }
      ]
    },
    {
      "name": "Frequency Health",
      "icon": "📅",
      "status": "alert",
      "insights": [
        {
          "title": "Meetings are monthly when they should be biweekly",
          "priority": "high",
          "body": "Seven meetings across approximately 7.5 months yields an average interval of 33 days. Gaps range from 28 days (shortest) to 56 days (Nov 17 to Jan 12, the longest — spanning the holiday period but still excessive). For a report with less than a year of tenure who is navigating career growth questions and workload stress, biweekly meetings are the minimum recommended cadence. Monthly meetings mean blockers sit unaddressed for weeks, career conversations get perpetually tabled for 'next time,' and small frustrations compound into big ones.",
          "evidence": "Intervals: Jul 14 → Aug 11 (28d), Aug 11 → Sep 8 (28d), Sep 8 → Oct 6 (28d), Oct 6 → Nov 17 (42d), Nov 17 → Jan 12 (56d), Jan 12 → Feb 24 (43d). Avg: 33 days.",
          "action": {
            "label": "Open Next Meeting With",
            "text": "Avery, I want to increase our meeting cadence. Monthly isn't frequent enough — especially right now with the workload conversation and your career growth questions. Can we go biweekly? I want to make sure things don't sit for a month before we address them.",
            "variant": "default"
          }
        },
        {
          "title": "Gaps widening at the worst possible time",
          "priority": "medium",
          "body": "The longest gaps (42 and 56 days) occurred during the October-January period — exactly when Avery was dealing with unresolved ad-hoc request frustration, a vague career conversation, and the onboarding launch. The meetings got less frequent as the stakes got higher. This is the opposite of what should happen.",
          "evidence": "Oct 6 → Nov 17: 42 days (ad-hoc still unresolved, career growth deferred). Nov 17 → Jan 12: 56 days (launch happened, perf review completed, no meeting to discuss either in real-time).",
          "action": {
            "label": "Manager Action",
            "text": "Block recurring biweekly 1-on-1s on the calendar now. Treat them as non-negotiable — the same way you'd treat a client meeting. If one needs to be rescheduled, reschedule within the same week rather than skipping.",
            "variant": "default"
          }
        }
      ]
    },
    {
      "name": "Meeting Structure",
      "icon": "📋",
      "status": "warn",
      "insights": [
        {
          "title": "Career topics consistently get squeezed out",
          "priority": "high",
          "body": "Career growth was raised or attempted in 3 of 7 meetings (Sep 8, Nov 17, Feb 24) but received meaningful time in zero of them. In September it 'ran out of time and was tabled.' In November it got vague feedback. In February it got another deferral. When career is always the last agenda item, it always gets cut. The fix is structural: put career first, at least once a month.",
          "evidence": "Sep 8: 'Ran out of time and it was tabled.' Avery's note: 'This is the second time career growth got pushed off.'",
          "action": {
            "label": "Manager Action",
            "text": "In the next 1-on-1, put career growth as the FIRST topic, not the last. Allocate 15 minutes minimum. Tell Avery in advance: 'I want to dedicate our first 15 minutes to your career development — come ready to talk about what you want.' This signals that it's a priority, not an afterthought.",
            "variant": "default"
          }
        },
        {
          "title": "No action item tracking between meetings",
          "priority": "medium",
          "body": "There is no evidence of action items being recorded or followed up across meetings. Morgan's commitments — 'let me think about the ad-hoc process,' 'I'll forward the review,' 'let me check with my manager' — have no visible tracking mechanism. This means follow-through depends entirely on memory, which is why commitments are dropping. A simple 'what did we each commit to last time?' at the start of each meeting would transform accountability.",
          "evidence": "Perf review promised in Jan 12, never confirmed as delivered. Morgan offered to attend research session in Aug 11, never attended. Ad-hoc request solution promised repeatedly before Nov resolution.",
          "action": {
            "label": "Suggested Discussion Topic",
            "text": "Avery, I want to try something new: at the end of each meeting, let's each write down 1-2 commitments, and at the start of the next meeting, we check in on them. I think this will help me follow through better. Would that be useful for you too?",
            "variant": "default"
          }
        }
      ]
    },
    {
      "name": "Autonomy Delegation",
      "icon": "🎯",
      "status": "good",
      "insights": [
        {
          "title": "Healthy autonomy on design decisions",
          "priority": "positive",
          "body": "For someone at ~11 months tenure, Avery demonstrates strong autonomy in design execution. Avery independently led user research, proposed the account/workspace merge, conducted competitive audits, chose the atomic design methodology for the design system, and created the Figma handoff that engineering praised. Morgan generally approves direction without micromanaging the how, which is appropriate for Avery's demonstrated competence level.",
          "evidence": "Design system goal was created by Avery (not Morgan). Onboarding research was run independently. Settings page competitive audit was self-directed.",
          "action": {
            "label": "Keep Doing",
            "text": "The level of design autonomy you're giving Avery is well-calibrated. Avery is driving technical decisions and proposing solutions. Keep this pattern — and use it as evidence in the promotion case.",
            "variant": "green"
          }
        },
        {
          "title": "Gap: no autonomy on workload and prioritization",
          "priority": "medium",
          "body": "While Avery has strong autonomy on design work, they have almost no autonomy on workload management. Avery can't say no to sales requests without Morgan's backing, can't deprioritize projects without Morgan's approval, and can't cut scope without Morgan's decision. This is appropriate — workload management is the manager's job — but it means Morgan needs to be more decisive and responsive when Avery raises capacity issues.",
          "evidence": "Oct 6: Avery felt it was 'unfair' to push back on sales VP without managerial cover. Feb 24: Avery wants scope cuts but can't make them unilaterally.",
          "action": {
            "label": "Manager Action",
            "text": "Give Avery explicit permission and language to protect their time: 'If anyone comes to you with an ad-hoc request, you are authorized to say: Morgan triages design requests weekly — please submit through the intake form. If they push back, send them to me.' Put this in writing.",
            "variant": "default"
          }
        }
      ]
    },
    {
      "name": "Personal Connection",
      "icon": "💬",
      "status": "warn",
      "insights": [
        {
          "title": "Zero personal touchpoints in 7 meetings",
          "priority": "medium",
          "body": "Across all seven documented meetings, there are no personal topics, no rapport-building conversation, and no life-context signals. Every meeting goes straight into project work. At 11 months, Morgan should know something about Avery as a person beyond their design output. This doesn't mean invasive personal questions — it means showing genuine interest in the human being, not just the designer. The absence of personal connection makes it harder to read burnout signals and harder for Avery to feel that Morgan genuinely cares about their wellbeing.",
          "evidence": "No meeting summaries, highlights, or individual notes contain any personal content — no references to weekends, interests, life events, or non-work topics.",
          "action": {
            "label": "Suggested Discussion Topic",
            "text": "Open the next meeting with 5 minutes of genuine personal conversation before diving into work. 'How was your weekend? What have you been up to outside of work?' And actually listen — don't rush to the agenda. If Avery shares something, remember it and follow up next time.",
            "variant": "default"
          }
        },
        {
          "title": "Mood data absent — flying blind on emotional state",
          "priority": "low",
          "body": "There is no mood tracking in any meeting. The only emotional signals come from Avery's private notes and the burnout disclosure in February. By the time burnout is explicitly stated, it's already advanced. Regular mood check-ins — even a simple 'how are you feeling on a 1-5 scale?' — would provide earlier warning signals.",
          "evidence": null,
          "action": {
            "label": "Suggested Discussion Topic",
            "text": "Avery, I want to start checking in on how you're feeling — not just what you're working on. At the start of each meeting, I'll ask: 'On a scale of 1-5, how's your energy this week?' No judgment, just a quick pulse so I can support you better.",
            "variant": "default"
          }
        }
      ]
    },
    {
      "name": "Talk Ratio",
      "icon": "🎙️",
      "status": "good",
      "insights": [
        {
          "title": "Avery drives most meeting content",
          "priority": "positive",
          "body": "Based on meeting content analysis, Avery is the primary driver of most discussions: demoing wireframes, presenting research findings, walking through designs, raising concerns, and asking career questions. Morgan's contributions are mostly reactive — approving directions, providing brief feedback, and making (often vague) commitments. While the talk ratio appears healthy, the issue isn't how much Morgan talks — it's the quality of what Morgan contributes when they do speak.",
          "evidence": "In 6 of 7 meetings, Avery initiates the key topics and presents work. Morgan's documented responses tend to be approvals or deferrals rather than substantive coaching.",
          "action": {
            "label": "Keep Doing",
            "text": "The report-driven dynamic is healthy. Avery is comfortable leading the conversation. The opportunity isn't to talk more — it's to make your contributions more substantive: specific feedback, clear decisions, concrete commitments.",
            "variant": "green"
          }
        }
      ]
    },
    {
      "name": "Team Dynamics",
      "icon": "👥",
      "status": "warn",
      "insights": [
        {
          "title": "Very narrow collaboration network",
          "priority": "medium",
          "body": "Avery's documented collaborators are limited: Suki (frontend engineer on the design system), the engineering lead (handoff recipient), and the PM (settings page feedback). There's no mention of other designers, no cross-team relationships, and no mentorship connections. For someone seeking a senior role, broader organizational visibility and peer relationships are important. The Product Design team is mentioned in Avery's profile but no teammates from that team appear in any data.",
          "evidence": "Only collaborator mentioned by name is Suki (work summaries from Jan-Feb). Engineering lead and PM mentioned by role only. No designer peers appear anywhere.",
          "action": {
            "label": "Suggested Discussion Topic",
            "text": "Avery, who are you learning from right now outside our immediate team? Are there other designers in the org you'd want to connect with? For your growth toward senior level, having relationships across the company would be valuable. I can help set up some introductions if that would be useful.",
            "variant": "default"
          }
        },
        {
          "title": "Suki collaboration is a bright spot worth protecting",
          "priority": "positive",
          "body": "The design system collaboration with Suki is one of the few cross-functional partnerships in the data and appears to be working well. This kind of designer-engineer partnership is exactly the type of senior-level collaboration that should be highlighted and expanded.",
          "evidence": "Work summary Jan 13-19: 'Partnered with Suki (frontend) to ensure Figma components match React implementations.'",
          "action": {
            "label": "Keep Doing",
            "text": "The partnership with Suki is great cross-functional work. Ask Avery how it's going and whether there's anything you can do to support it — dedicated pairing time, shared goals, or co-presenting the design system to the broader team.",
            "variant": "green"
          }
        }
      ]
    },
    {
      "name": "Expectations Tracker",
      "icon": "📊",
      "status": "warn",
      "insights": [
        {
          "title": "Onboarding goal completed and exceeded — celebrate it",
          "priority": "medium",
          "body": "The onboarding redesign goal hit 100% with an 18% activation rate improvement against a 15% target. This goal had healthy check-in cadence (Aug, Oct, Jan) and was clearly tracked. It's a model for how goals should work. The gap: it was never formally celebrated or used as promotion evidence.",
          "evidence": "Goal 3201: 100% complete. Check-in Jan 12: 'Launched Dec 16. Activation rate up 18% — exceeded the 15% target!'",
          "action": {
            "label": "Manager Action",
            "text": "Use this completed goal as the centerpiece of Avery's promotion case. Document the full narrative: end-to-end ownership, user research, stakeholder alignment, quality handoff, measurable business impact. This is exactly what 'consistent impact' looks like.",
            "variant": "default"
          }
        },
        {
          "title": "Settings page goal overdue with no timeline reset",
          "priority": "medium",
          "body": "The settings page redesign goal was due January 31 and is at 60% progress. The delay was acknowledged in August ('fine to push to September') but the due date was never formally updated, and no new timeline was discussed. The goal is technically overdue by over a month with no clear completion date. This creates ambiguity about expectations.",
          "evidence": "Goal 3202: 60% progress, due date 2026-01-31 (35 days overdue). Settings page phase 2 mentioned in Jan 12 meeting as Q1 priority.",
          "action": {
            "label": "Suggested Discussion Topic",
            "text": "Avery, the settings page goal was due end of January and we're at 60%. Let's reset the timeline to something realistic given your other commitments. When do you think you can finish phase 1, and should we scope phase 2 as a separate goal?",
            "variant": "default"
          }
        },
        {
          "title": "Design system goal at risk — 40% done with 2 months left",
          "priority": "medium",
          "body": "The design system goal is at 40% (12/30 components) with a due date of April 30. At the current pace — roughly 7 components per month — Avery would need 2.5 more months to complete the remaining 18 components, making the April 30 deadline tight even without the competing workstreams. With settings page bugs and ad-hoc leakage, this goal is at risk.",
          "evidence": "Goal 3203: 40% progress, due 2026-04-30. Work summary Feb 17-23: 'pace is slower than projected — carrying too many parallel workstreams.'",
          "action": {
            "label": "Suggested Discussion Topic",
            "text": "Avery, the design system is at 12 of 30 components with about 2 months left. If we clear your plate of settings page bugs and ad-hoc leakage, is the April deadline achievable? Or should we scope it to 20 core components and add the rest in a phase 2?",
            "variant": "default"
          }
        }
      ]
    }
  ],
  "agenda": {
    "next_meeting": [
      {
        "topic": "Career growth: definitive answer on senior designer promotion",
        "tags": ["career", "retention-risk"],
        "opener": "Avery, I owe you a straight answer on the senior designer promotion. You've been asking since September and I've been vague. Here's where things actually stand..."
      },
      {
        "topic": "Workload triage: cut scope to address burnout",
        "tags": ["workload", "burnout", "decision"],
        "opener": "Last time you told me you were burning out and I suggested a long weekend. That wasn't enough. Let's actually decide what comes off your plate today. Here's my proposal..."
      },
      {
        "topic": "Recognition: onboarding launch visibility",
        "tags": ["recognition", "visibility"],
        "opener": "Avery, your onboarding redesign drove an 18% activation lift and I haven't done enough to make that visible. How would you feel about presenting the results at the next all-hands? This is work the whole company should see."
      },
      {
        "topic": "Performance review: share the written review",
        "tags": ["feedback", "follow-through"],
        "opener": "I sent you the written performance review — did you get a chance to look at it? I want to walk through it together and make sure nothing is unclear."
      },
      {
        "topic": "Design system goal: timeline reality check",
        "tags": ["goals", "planning"],
        "opener": "The design system is at 12 of 30 components with April 30 as the deadline. If we protect your focus time, is that realistic? Or should we scope it differently?"
      },
      {
        "topic": "Meeting cadence: move to biweekly",
        "tags": ["process", "frequency"],
        "opener": "I want to meet more often — biweekly instead of monthly. Too many important things are sitting for weeks between our meetings. Does that work for you?"
      },
      {
        "topic": "Ad-hoc request leakage: close the gap",
        "tags": ["blocker", "process"],
        "opener": "The intake process helped but requests are still leaking through. What's slipping past? Let me tighten things up on my end."
      },
      {
        "topic": "Personal check-in: how are you doing beyond work?",
        "tags": ["personal", "rapport"],
        "opener": "Before we get into everything — how are you doing? Not work stuff. How's life outside of Nimbus?"
      }
    ],
    "phased_plan": [
      {
        "meeting": "Meeting 1 — Repair & Commit",
        "theme": "Address the trust debt: career clarity, workload decisions, and recognition",
        "topics": [
          "Open with personal check-in (5 min) — build the human connection that's been missing",
          "Career growth: share the promotion decision or a concrete written development plan with specific criteria and timeline (15 min)",
          "Workload triage: present a proposal to cut settings page bugs and contain ad-hoc leakage, get Avery's input, decide together (10 min)",
          "Recognition: propose the all-hands presentation for onboarding results and give a written recognition for the launch impact (5 min)",
          "Commit to biweekly cadence and action item tracking starting now (5 min)"
        ]
      },
      {
        "meeting": "Meeting 2 — Stabilize & Focus",
        "theme": "Verify the workload fix is working and establish sustainable pace",
        "topics": [
          "Review action items from Meeting 1 — did the workload cuts happen? Is the written review shared?",
          "Check energy and burnout: is the pace feeling better? Are evenings free again?",
          "Design system progress check: is the April timeline achievable with the reduced load?",
          "Settings page goal: reset the timeline formally and update the due date",
          "Discuss broader team connections: who else should Avery be collaborating with or getting visibility from?"
        ]
      },
      {
        "meeting": "Meeting 3 — Growth & Forward",
        "theme": "Shift from crisis management to proactive career development",
        "topics": [
          "Career development plan review: check progress against the specific criteria established in Meeting 1",
          "Explore growth interests: user research practice, design systems leadership, mentoring — what does Avery want to lean into?",
          "Create a growth-oriented goal: co-create one goal focused on senior-level skill development",
          "Give constructive feedback using SBI framework on one specific area for growth",
          "Discuss long-term vision: where does Avery see themselves in 1-2 years, and how can Morgan help get there?"
        ]
      }
    ]
  }
}
